import { Link } from 'react-router-dom';
import { useAdmins, useFlags } from '@/hooks/useConsoleData';
import { Section } from '@/components/console/Section';
import { useAuthStore } from '@/store/authStore';
import { NOT_MEASURED, when } from '@/lib/format';
import { APP_URL } from '@/constants/common';
import s from './console.module.css';

/**
 * Settings: who can open this (read only), when the digest posts, and the
 * links to the Slack channels and GHL. Nothing on this page changes anything;
 * flags and admins are changed in the environment and the database.
 *
 * On a phone the first section is the way to the pages the tab bar does not
 * carry (Today, Messages, Product, System), the app, and Sign out. It is
 * hidden at 640px and above, where the top bar has all of that.
 */
const PHONE_PAGES = [
  { to: '/', label: 'Today', what: 'the action list' },
  { to: '/business', label: 'Business', what: 'MRR, churn, LTV, founding members, refunds, sign-up week retention' },
  { to: '/help', label: 'Help', what: 'help requests from the app, with the reply staged on each' },
  { to: '/messages', label: 'Messages', what: 'every note sent, queued or failed' },
  { to: '/product', label: 'Product', what: 'usage, errors, flags' },
  { to: '/system', label: 'System', what: 'the watchtower board' },
  { to: '/reliability', label: 'Reliability', what: 'the error budget per surface; over 1.0 only fixes merge there' },
  { to: '/monitoring', label: 'What we monitor', what: 'every signal we save, its flag and today\'s counts, and members to learn from' },
];
const slackUrl = (channelId: string) => `https://slack.com/app_redirect?channel=${encodeURIComponent(channelId)}`;
const ghlUrl = (locationId: string) => `https://app.gohighlevel.com/v2/location/${encodeURIComponent(locationId)}/contacts/smart_list/All`;

export default function SettingsPage() {
  const admins = useAdmins();
  const flags = useFlags();
  const { admin, logout } = useAuthStore();
  const f = flags.data;
  const rows = admins.data ?? [];
  const superAdmins = rows.filter((a) => a.role === 'SUPER_ADMIN');

  return (
    <div className={s.stack}>
      <div className={s.pageHead}>
        <h1 className={s.pageTitle}>Settings</h1>
        <span className={s.pageSub}>signed in as {admin?.email ?? NOT_MEASURED}</span>
      </div>

      <div className={s.phoneOnly} style={{ flexDirection: 'column' }} data-panel="phone-pages">
        <Section title="Other pages" summary="the tabs carry People, Needs and Growth; the rest is here" defaultOpen>
          <div className={s.list}>
            {PHONE_PAGES.map((pg) => (
              <Link key={pg.to} to={pg.to} className={s.stopRow}><span><span className={s.stopLabel}>{pg.label}</span><span className={s.sub}>{pg.what}</span></span></Link>
            ))}
          </div>
          <div className={s.draftActions} style={{ marginTop: 12 }}>
            <a className={s.btnGhost} href={APP_URL} target="_blank" rel="noreferrer">Open the app</a>
            <button type="button" className={s.btnGhost} onClick={logout}>Sign out</button>
          </div>
        </Section>
      </div>

      <div className={s.strip}>
        <div className={s.stat}><span className={s.statLabel}>Can open this</span><span className={s.statVal}>{admins.data ? superAdmins.length : NOT_MEASURED}</span><span className={s.statSub}>SUPER_ADMIN accounts; nobody else can</span></div>
        <div className={s.stat}><span className={s.statLabel}>Daily digest</span><span className={s.statVal}>{f ? (f.digest.enabled ? 'on' : 'off') : NOT_MEASURED}</span><span className={s.statSub}>{f ? `${String(f.digest.hourUtc).padStart(2, '0')}:00 UTC, once a day, to ${f.digest.channels.length} ${f.digest.channels.length === 1 ? 'channel' : 'channels'}` : 'reading'}</span></div>
      </div>

      <Section title="Admins" summary="read only; roles are changed in the database" count={rows.length} defaultOpen>
        {admins.isError && <div className={s.error}>Could not load the admin list (GET /admin/admins).</div>}
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead><tr><th>Email</th><th>Name</th><th>Role</th><th>2FA</th><th>Since</th></tr></thead>
            <tbody>
              {admins.data && rows.length === 0 && <tr><td colSpan={5}>No admin accounts returned.</td></tr>}
              {rows.map((a) => (
                <tr key={a.id}>
                  <td>{a.email}{a.isSuspended ? <span className={s.sub}> · suspended</span> : ''}</td>
                  <td>{`${a.firstName ?? ''} ${a.lastName ?? ''}`.trim() || ''}</td>
                  <td><span className={`${s.pill} ${a.role === 'SUPER_ADMIN' ? s.pill_on : ''}`}>{a.role}</span></td>
                  <td>{a.twoFactorEnabled === undefined ? NOT_MEASURED : a.twoFactorEnabled ? 'on' : 'off'}</td>
                  <td>{when(a.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={s.sub} style={{ marginTop: 8 }}>Only SUPER_ADMIN opens admin.posture.vision and the feeds behind it. Other admin roles exist for the API and see nothing here.</div>
      </Section>

      <Section title="Digest schedule" summary={f ? `${f.digest.enabled ? 'posting' : 'not posting'} at ${String(f.digest.hourUtc).padStart(2, '0')}:00 UTC` : 'reading'} defaultOpen>
        {flags.isError && <div className={s.error}>Could not read the digest settings (GET /provider/admin/flags).</div>}
        {f && (
          <dl className={s.facts}>
            <dt>Enabled</dt><dd>{f.digest.enabled ? 'yes (PRODUCT_DIGEST_ENABLED=true)' : 'no (PRODUCT_DIGEST_ENABLED is not true)'}</dd>
            <dt>Hour</dt><dd>{String(f.digest.hourUtc).padStart(2, '0')}:00 UTC (PRODUCT_DIGEST_HOUR_UTC)</dd>
            <dt>Channels</dt><dd>{f.digest.channels.length ? f.digest.channels.map((c) => <span key={c}><a className={s.linkA} href={slackUrl(c)} target="_blank" rel="noreferrer">{c}</a>{' '}</span>) : 'none configured'}</dd>
            <dt>Same numbers</dt><dd>The Today strip reads GET /provider/admin/digest, the same collector the Slack post uses.</dd>
          </dl>
        )}
      </Section>

      <Section title="Links" summary="Slack channels and GHL" defaultOpen>
        <dl className={s.facts}>
          <dt>Support channel</dt><dd>{f?.slack.supportChannelId ? <a className={s.linkA} href={slackUrl(f.slack.supportChannelId)} target="_blank" rel="noreferrer">{f.slack.supportChannelId}</a> : NOT_MEASURED}</dd>
          <dt>Growth channel</dt><dd>{f?.slack.growthChannelId ? <a className={s.linkA} href={slackUrl(f.slack.growthChannelId)} target="_blank" rel="noreferrer">{f.slack.growthChannelId}</a> : NOT_MEASURED}</dd>
          <dt>Product channel</dt><dd>{f?.slack.productChannelId ? <a className={s.linkA} href={slackUrl(f.slack.productChannelId)} target="_blank" rel="noreferrer">{f.slack.productChannelId}</a> : 'not configured'}</dd>
          <dt>GHL contacts</dt><dd>{f?.ghl.locationId ? <a className={s.linkA} href={ghlUrl(f.ghl.locationId)} target="_blank" rel="noreferrer">open the location</a> : NOT_MEASURED}</dd>
          <dt>Status page</dt><dd><a className={s.linkA} href="https://www.posture.vision/status" target="_blank" rel="noreferrer">www.posture.vision/status</a></dd>
          <dt>ATLAS</dt><dd><a className={s.linkA} href="https://marketing.posture.vision" target="_blank" rel="noreferrer">marketing.posture.vision</a></dd>
        </dl>
      </Section>
    </div>
  );
}
